import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../services/api.js';
import { useAuth } from '../../context/AuthContext.jsx';

function fmt(ts) {
  return ts ? new Date(ts).toLocaleTimeString() : '—';
}

function markerIcon(asset, selected) {
  const color = asset.assetType === 'tug' ? '#0d6efd' : '#198754';
  const ring = selected ? 'box-shadow:0 0 0 4px rgba(255,193,7,.8);' : '';
  const icon = asset.assetType === 'tug' ? 'fa-ship' : 'fa-anchor';
  return L.divIcon({
    className: '',
    html: `<div data-testid="map-marker" data-name="${asset.name}" title="${asset.name}"
      style="width:28px;height:28px;border-radius:50%;background:${color};${ring}
      display:flex;align-items:center;justify-content:center;color:#fff;border:2px solid #fff;">
      <i class="fas ${icon}" style="font-size:12px;"></i></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

export default function LiveTrackingPage() {
  const { hasCapability } = useAuth();
  const canWrite = hasCapability('operations_write');

  const [snapshot, setSnapshot] = useState({ assets: [], zones: [], geofenceLogs: [] });
  const [selectedId, setSelectedId] = useState(null);
  const [showTugs, setShowTugs] = useState(true);
  const [showVessels, setShowVessels] = useState(true);
  const [portFilter, setPortFilter] = useState(''); // '' = all serviced ports
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);
  const [zoneId, setZoneId] = useState('');
  const [eventType, setEventType] = useState('Enter');
  const [recording, setRecording] = useState(false);

  // Operations exception watch.
  const [exceptions, setExceptions] = useState([]);
  const [reassignJobId, setReassignJobId] = useState(null);
  const [backupTugs, setBackupTugs] = useState([]);
  const [backupTugId, setBackupTugId] = useState('');
  const [excBusy, setExcBusy] = useState(false);

  const mapRef = useRef(null);
  const mapEl = useRef(null);
  const layerRef = useRef(null);

  const load = useCallback(async () => {
    const [res, exc] = await Promise.all([api.getTrackingSnapshot(), api.getExceptions().catch(() => [])]);
    setSnapshot(res || { assets: [], zones: [], geofenceLogs: [] });
    setExceptions(exc || []);
    setZoneId((prev) => prev || (res?.zones?.[0]?.id ?? ''));
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load tracking telemetry.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  // Initialise the Leaflet map once.
  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, { center: [12.5, 122.0], zoom: 6, scrollWheelZoom: false });
    // Colored basemap (CARTO Voyager) so the markers sit on an actual map with
    // coastlines, land/water colour and labels. Same cartocdn host as before, so
    // the CSP img-src allowance already covers it.
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    // Leaflet computes marker pixel positions from the container size at init.
    // On SPA navigation / flex layout the container can still be 0×0 then, which
    // paints every marker off-screen (blank blue map). Recalculate once the
    // browser has laid the element out, and on any later resize.
    const recalc = () => map.invalidateSize();
    const raf = requestAnimationFrame(recalc);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(recalc) : null;
    ro?.observe(mapEl.current);
    window.addEventListener('resize', recalc);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener('resize', recalc);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Serviced ports for the filter dropdown (sorted), sourced from the bases.
  const ports = useMemo(
    () => [...snapshot.zones].sort((a, b) => a.name.localeCompare(b.name)),
    [snapshot.zones]
  );

  const visibleAssets = useMemo(
    () =>
      snapshot.assets.filter(
        (a) =>
          ((a.assetType === 'tug' && showTugs) || (a.assetType === 'vessel' && showVessels)) &&
          (!portFilter || a.portId === portFilter)
      ),
    [snapshot.assets, showTugs, showVessels, portFilter]
  );

  // Only the selected port's geofence circle when filtered; all bases otherwise.
  const visibleZones = useMemo(
    () => (portFilter ? snapshot.zones.filter((z) => z.id === portFilter) : snapshot.zones),
    [snapshot.zones, portFilter]
  );

  // Re-render the markers whenever data/selection/visibility changes.
  useEffect(() => {
    const group = layerRef.current;
    if (!group) return;
    group.clearLayers();

    visibleZones.forEach((z) => {
      L.circle([z.latitude, z.longitude], {
        radius: 4000,
        color: '#6c757d',
        weight: 1,
        fillColor: '#6c757d',
        fillOpacity: 0.08,
      })
        .bindTooltip(`Geofence: ${z.name}`)
        .addTo(group);
    });

    visibleAssets.forEach((a) => {
      L.marker([a.latitude, a.longitude], { icon: markerIcon(a, a.id === selectedId) })
        .on('click', () => setSelectedId(a.id))
        .bindTooltip(`${a.name} · ${a.speedKnots} kn · ${a.headingDeg}°`)
        .addTo(group);
    });
  }, [visibleZones, visibleAssets, selectedId]);

  // Frame the view to the plotted zones + assets so the fleet is always on
  // screen regardless of the container size at init. Keyed on data only (not
  // selection) so clicking a marker doesn't re-zoom the map. Re-fits when the
  // port filter narrows the set, zooming into the chosen port.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const pts = [
      ...visibleZones.map((z) => [z.latitude, z.longitude]),
      ...visibleAssets.map((a) => [a.latitude, a.longitude]),
    ].filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
    if (!pts.length) return;
    map.invalidateSize();
    map.fitBounds(L.latLngBounds(pts), { padding: [40, 40], maxZoom: portFilter ? 12 : 11 });
  }, [visibleZones, visibleAssets, portFilter]);

  // Scope the exceptions/SLA watch to the selected port too (by the tug/vessel
  // belonging to that port), so the whole console reads as one port's picture.
  const visibleExceptions = useMemo(() => {
    if (!portFilter) return exceptions;
    const names = new Set(
      snapshot.assets.filter((a) => a.portId === portFilter).map((a) => a.name)
    );
    return exceptions.filter((e) => names.has(e.tugName) || names.has(e.vesselName));
  }, [exceptions, snapshot.assets, portFilter]);

  const selected = snapshot.assets.find((a) => a.id === selectedId) || null;

  const recordGeofence = async () => {
    if (!selected || !zoneId) return;
    setRecording(true);
    setPageMsg(null);
    try {
      const logs = await api.recordGeofence(selected.id, { zoneId, eventType });
      setSnapshot((s) => ({ ...s, geofenceLogs: logs }));
      const zoneName = snapshot.zones.find((z) => z.id === zoneId)?.name || 'zone';
      setPageMsg({
        type: 'info',
        text: `Geofence transit logged: ${selected.name} ${eventType} ${zoneName}.`,
      });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to log geofence transit.' });
    } finally {
      setRecording(false);
    }
  };

  const openReassign = async (exc) => {
    setReassignJobId(exc.jobId);
    setBackupTugs([]);
    setBackupTugId('');
    setPageMsg(null);
    try {
      const tugs = await api.getExceptionBackupTugs(exc.jobId);
      setBackupTugs(tugs || []);
      setBackupTugId(tugs?.[0]?.id ?? '');
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to load backup tugs.' });
    }
  };

  const confirmReassign = async () => {
    if (!reassignJobId || !backupTugId) return;
    setExcBusy(true);
    setPageMsg(null);
    try {
      const res = await api.reassignExceptionJob(reassignJobId, { toTugId: backupTugId });
      setExceptions(res.exceptions || []);
      setReassignJobId(null);
      setPageMsg({
        type: res.cleared ? 'success' : 'warning',
        text: res.cleared
          ? 'Job reassigned to the backup tug — exception alarm cleared.'
          : 'Job reassigned, but the alarm is still active.',
      });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to reassign.' });
    } finally {
      setExcBusy(false);
    }
  };

  const contactTug = async (exc) => {
    setExcBusy(true);
    setPageMsg(null);
    try {
      await api.contactExceptionTug(exc.jobId);
      setPageMsg({ type: 'info', text: `Contacting ${exc.tugName || 'tug'} via push + SMS…` });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to contact tug.' });
    } finally {
      setExcBusy(false);
    }
  };

  return (
    <div className="container-fluid py-4" data-testid="live-tracking-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-map-marked-alt me-2" aria-hidden="true" />
                Live Tracking Map &amp; Operations Console
              </h3>
              <p className="text-muted mb-0 small">
                Real-time situational awareness of active jobs, tug positions (GPS), and vessel
                targets (AIS).
              </p>
            </div>
            <span className="badge bg-success p-2">
              <i className="fas fa-satellite me-1" aria-hidden="true" />
              AIS Ingestion Rate: 10s
            </span>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="tracking-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="tracking-message">
          {pageMsg.text}
        </div>
      )}

      <div className="row">
        {/* LEFT: map + roster */}
        <div className="col-lg-8 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center gap-3 mb-2 small">
                <div className="d-flex align-items-center gap-2">
                  <label htmlFor="portFilter" className="fw-bold text-muted mb-0">
                    <i className="fas fa-anchor me-1" aria-hidden="true" />
                    Port / Region
                  </label>
                  <select
                    id="portFilter"
                    className="form-select form-select-sm"
                    style={{ width: 'auto' }}
                    value={portFilter}
                    onChange={(e) => {
                      setPortFilter(e.target.value);
                      setSelectedId(null);
                    }}
                    data-testid="port-filter"
                  >
                    <option value="">All serviced ports ({ports.length})</option>
                    {ports.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="layerTugs"
                    checked={showTugs}
                    onChange={(e) => setShowTugs(e.target.checked)}
                    data-testid="layer-tugs"
                  />
                  <label className="form-check-label fw-bold" htmlFor="layerTugs">
                    Show Active Tugs (GPS)
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="layerVessels"
                    checked={showVessels}
                    onChange={(e) => setShowVessels(e.target.checked)}
                    data-testid="layer-vessels"
                  />
                  <label className="form-check-label fw-bold" htmlFor="layerVessels">
                    Show Client Ships (AIS)
                  </label>
                </div>
              </div>
              <div
                ref={mapEl}
                data-testid="tracking-map"
                style={{ height: 420, width: '100%', borderRadius: 8, background: '#e6ecf0' }}
              />
              {loading && <p className="text-muted small mt-2 mb-0">Loading telemetry…</p>}
            </div>
          </div>

          {/* Asset roster — clickable list mirroring the map markers */}
          <div className="card border-0 shadow-sm mt-3">
            <div className="card-header bg-white pt-3 border-bottom-0">
              <h6 className="mb-0 text-secondary">
                <i className="fas fa-list me-2" aria-hidden="true" />
                Tracked Assets
              </h6>
            </div>
            <div className="list-group list-group-flush">
              {visibleAssets.map((a) => (
                <button
                  type="button"
                  key={a.id}
                  className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                    a.id === selectedId ? 'active' : ''
                  }`}
                  onClick={() => setSelectedId(a.id)}
                  data-testid="asset-row"
                  data-name={a.name}
                >
                  <span>
                    <i
                      className={`fas ${a.assetType === 'tug' ? 'fa-ship' : 'fa-anchor'} me-2`}
                      aria-hidden="true"
                    />
                    {a.name}
                    <span className={`badge ms-2 ${a.assetType === 'tug' ? 'bg-primary' : 'bg-success'}`}>
                      {a.assetType.toUpperCase()}
                    </span>
                  </span>
                  <span className="small">{a.speedKnots} kn</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: selected detail + geofence log */}
        <div className="col-lg-4 mb-4">
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-header bg-white pt-4 border-bottom-0">
              <h5 className="mb-0 text-secondary">
                <i className="fas fa-crosshairs me-2 text-info" aria-hidden="true" />
                Asset Telemetry
              </h5>
            </div>
            <div className="card-body">
              {!selected ? (
                <div className="text-center text-muted py-4" data-testid="no-asset">
                  <i className="fas fa-satellite-dish fa-2x mb-2" aria-hidden="true" />
                  <p className="small mb-0">Click a marker or asset to view live telemetry.</p>
                </div>
              ) : (
                <div data-testid="asset-detail">
                  <h6 className="fw-bold">{selected.name}</h6>
                  <ul className="list-group list-group-flush small mb-3">
                    <li className="list-group-item d-flex justify-content-between p-2">
                      <span className="text-muted">Type</span>
                      <strong>{selected.assetType === 'tug' ? 'Owned Tug (GPS)' : 'Client Vessel (AIS)'}</strong>
                    </li>
                    <li className="list-group-item d-flex justify-content-between p-2">
                      <span className="text-muted">Port / Region</span>
                      <strong data-testid="asset-port">{selected.port || '—'}</strong>
                    </li>
                    <li className="list-group-item d-flex justify-content-between p-2">
                      <span className="text-muted">Speed</span>
                      <strong data-testid="asset-speed">{selected.speedKnots} kn</strong>
                    </li>
                    <li className="list-group-item d-flex justify-content-between p-2">
                      <span className="text-muted">Heading</span>
                      <strong data-testid="asset-heading">{selected.headingDeg}°</strong>
                    </li>
                    <li className="list-group-item d-flex justify-content-between p-2">
                      <span className="text-muted">Status</span>
                      <strong data-testid="asset-status">{selected.status}</strong>
                    </li>
                    <li className="list-group-item d-flex justify-content-between p-2">
                      <span className="text-muted">Active Job</span>
                      <strong data-testid="asset-job">
                        {selected.jobRef ? `${selected.jobRef} (${selected.jobStatus})` : 'None'}
                      </strong>
                    </li>
                    <li className="list-group-item d-flex justify-content-between p-2">
                      <span className="text-muted">Last Fix</span>
                      <strong>{fmt(selected.recordedAt)}</strong>
                    </li>
                  </ul>

                  {canWrite && (
                    <div className="border-top pt-3" data-testid="geofence-control">
                      <label className="form-label small fw-bold text-muted">Log Geofence Transit</label>
                      <div className="input-group input-group-sm mb-2">
                        <select
                          className="form-select"
                          value={zoneId}
                          onChange={(e) => setZoneId(e.target.value)}
                          data-testid="geofence-zone"
                        >
                          {snapshot.zones.map((z) => (
                            <option key={z.id} value={z.id}>
                              {z.name}
                            </option>
                          ))}
                        </select>
                        <select
                          className="form-select"
                          value={eventType}
                          onChange={(e) => setEventType(e.target.value)}
                          data-testid="geofence-event"
                        >
                          <option value="Enter">Enter</option>
                          <option value="Exit">Exit</option>
                        </select>
                      </div>
                      <button
                        className="btn btn-sm btn-outline-primary w-100"
                        onClick={recordGeofence}
                        disabled={recording || !zoneId}
                        data-testid="log-geofence"
                      >
                        {recording && (
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        )}
                        Log Transit
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white pt-4 border-bottom-0">
              <h5 className="mb-0 text-secondary">
                <i className="fas fa-route me-2 text-warning" aria-hidden="true" />
                Geofence Transit Log
              </h5>
            </div>
            <div className="list-group list-group-flush" style={{ maxHeight: 260, overflowY: 'auto' }}>
              {snapshot.geofenceLogs.length === 0 && (
                <div className="text-center text-muted py-4 small" data-testid="no-geofence-logs">
                  No geofence transits logged yet.
                </div>
              )}
              {snapshot.geofenceLogs.map((g) => (
                <div className="list-group-item small" key={g.id} data-testid="geofence-log-row">
                  <span
                    className={`badge me-2 ${g.eventType === 'Enter' ? 'bg-success' : 'bg-secondary'}`}
                  >
                    {g.eventType}
                  </span>
                  <strong>{g.assetName}</strong> — {g.zoneName}
                  <span className="text-muted d-block">{fmt(g.loggedAt)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Operations Exception Watch (UC 92c4d92f) */}
      <div className="card border-0 shadow-sm mb-4" data-testid="exceptions-panel">
        <div className="card-header bg-white pt-4 border-bottom-0 d-flex justify-content-between align-items-center">
          <h5 className="mb-0 text-secondary">
            <i className="fas fa-triangle-exclamation me-2 text-warning" aria-hidden="true" />
            Real-time Exceptions &amp; SLA Watch
          </h5>
          <span
            className={`badge p-2 ${visibleExceptions.length > 0 ? 'bg-danger' : 'bg-success'}`}
            data-testid="exceptions-count"
          >
            {visibleExceptions.length > 0 ? `${visibleExceptions.length} Active Alarm(s)` : 'All Clear'}
          </span>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light text-secondary small text-uppercase">
                <tr>
                  <th>Alert ID</th>
                  <th>Job / Vessel</th>
                  <th>Error Category</th>
                  <th>Severity</th>
                  <th className="text-end">Quick Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleExceptions.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4" data-testid="no-exceptions">
                      {portFilter
                        ? 'No active exceptions at this port.'
                        : 'No active operational exceptions.'}
                    </td>
                  </tr>
                )}
                {visibleExceptions.map((e) => (
                  <tr key={e.alertId} data-testid="exception-row" data-ref={e.bookingRef || e.alertId}>
                    <td className="fw-bold">{e.alertId}</td>
                    <td className="small">
                      {e.bookingRef || '—'}
                      <span className="d-block text-muted">{e.vesselName || e.tugName || '—'}</span>
                    </td>
                    <td>
                      <span className="badge bg-warning text-dark" data-testid="exception-category">
                        {e.category}
                      </span>
                      <span className="d-block text-muted small mt-1">{e.detail}</span>
                    </td>
                    <td>
                      <span
                        className={`badge ${e.severity === 'Critical' ? 'bg-danger' : 'bg-secondary'}`}
                        data-testid="exception-severity"
                      >
                        {e.severity}
                      </span>
                    </td>
                    <td className="text-end">
                      {e.jobId && canWrite ? (
                        reassignJobId === e.jobId ? (
                          <div className="d-inline-flex align-items-center gap-1" data-testid="reassign-form">
                            <select
                              className="form-select form-select-sm"
                              style={{ width: 150 }}
                              value={backupTugId}
                              onChange={(ev) => setBackupTugId(ev.target.value)}
                              data-testid="backup-tug"
                            >
                              {backupTugs.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name}
                                </option>
                              ))}
                            </select>
                            <button
                              className="btn btn-sm btn-success"
                              onClick={confirmReassign}
                              disabled={excBusy || !backupTugId}
                              data-testid="confirm-reassign"
                            >
                              Confirm
                            </button>
                            <button
                              className="btn btn-sm btn-link text-muted"
                              onClick={() => setReassignJobId(null)}
                              data-testid="cancel-reassign"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="d-inline-flex gap-1">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => contactTug(e)}
                              disabled={excBusy}
                              data-testid="contact-tug"
                            >
                              <i className="fas fa-phone me-1" aria-hidden="true" />
                              Contact Tug
                            </button>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openReassign(e)}
                              disabled={excBusy}
                              data-testid="manual-reassign"
                            >
                              <i className="fas fa-shuffle me-1" aria-hidden="true" />
                              Manual Reassign
                            </button>
                          </div>
                        )
                      ) : (
                        <span className="text-muted small">Monitor</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
